import { Container, Graphics, Rectangle, Ticker } from 'pixi.js';
import { AssetLoader } from '../../asset/AssetLoader';
import type { Random } from '../../core/Random';
import { Easings, tween, type TweenHandle } from '../../core/Tween';
import { SlotReelHoldUI } from './SlotReelHoldUI';
import { SlotSymbol } from './SlotSymbol';
import type { SlotSymbolDef } from './types';

/**
 * A single vertical reel of N visible symbols. Internally maintains a strip of
 * (visibleRows + 2) symbols so symbols scrolling out the bottom can be replaced
 * at the top for an infinite-scroll illusion. Free-spin runs at constant speed
 * via the Pixi ticker; {@link stop} eases to a target offset that shows the
 * given final symbols.
 *
 * <p>Lifted in spirit from cs-core's
 * {@code DisplayReelsAnimator} + {@code MovingSymbol} pattern. The full
 * symbol-paths / hold-logic model is deferred to a follow-up chunk.
 */
export class SlotReel extends Container {
  private readonly strip: Container;
  /** Symbols on the strip, top-to-bottom. */
  private symbols: SlotSymbol[] = [];
  private isSpinning = false;
  private isStopping = false;
  private freeSpinHandler: ((tick: Ticker) => void) | null = null;
  private stopHandle: TweenHandle | null = null;
  /** Brief "thunk" settle tween started right after the stop animation
   *  lands. Tracked here so {@link destroyReel} can cancel it on
   *  teardown — without this guard, an in-flight thunk tick after
   *  {@code strip.destroy()} throws "Cannot set properties of null
   *  (setting 'y')" (Pixi nulls out internal transform trackers on
   *  destroy). */
  private thunkHandle: TweenHandle | null = null;

  // Hold-reel state. The visual chrome (HOLD pill + highlight frame) lives in
  // SlotReelHoldUI; the reel just tracks the boolean and the interactivity
  // flag that gates tap handling.
  private held = false;
  private holdInteractive = false;
  private readonly holdUI: SlotReelHoldUI;
  /** Tap on the reel toggles {@link held}. Host scene listens here so it can
   * surface haptic / SFX feedback or update HUD. Set null to ignore. */
  public onHoldToggle: ((held: boolean) => void) | null = null;

  /** Queue of symbol defs that {@link advanceStrip} consumes before falling back
   * to {@link weightedPick}. {@link stop} populates this so the target symbols
   * scroll into view through the natural wrap mechanism — no instant swap, no
   * flicker at the moment of landing. */
  private wrapQueue: SlotSymbolDef[] = [];

  constructor(
    private readonly ticker: Ticker,
    private readonly random: Random,
    private readonly availableSymbols: readonly SlotSymbolDef[],
    private readonly visibleRows: number,
    private readonly symbolSize: number,
    private readonly scrollSpeed: number,
    accentColor?: number,
  ) {
    super();

    // Mask: visible window is exactly visibleRows tall, symbolSize wide.
    const mask = new Graphics()
      .rect(-symbolSize / 2, 0, symbolSize, symbolSize * visibleRows)
      .fill({ color: 0xffffff });
    this.addChild(mask);
    this.mask = mask;

    this.strip = new Container();
    this.addChild(this.strip);

    // (visibleRows + 2): one extra above to handle the wrap, one extra below for smoothness.
    const stripCount = visibleRows + 2;
    for (let i = 0; i < stripCount; i++) {
      const symbol = new SlotSymbol(this.weightedPick(), symbolSize);
      symbol.x = 0;
      symbol.y = (i - 1) * symbolSize + symbolSize / 2;
      this.strip.addChild(symbol);
      this.symbols.push(symbol);
    }

    // Hold UI: HOLD pill + highlight frame, owned as a single child so the
    // reel doesn't need to manage their lifecycle individually.
    this.holdUI = new SlotReelHoldUI(symbolSize, visibleRows, accentColor);
    this.addChild(this.holdUI);

    // Tap interaction. eventMode toggles via setHoldInteractive(); the hit area
    // covers the visible window so taps anywhere on the reel toggle the hold.
    this.hitArea = new Rectangle(
      -symbolSize / 2,
      0,
      symbolSize,
      symbolSize * visibleRows,
    );
    this.on('pointertap', () => {
      if (!this.holdInteractive) return;
      this.setHeld(!this.held);
      this.onHoldToggle?.(this.held);
    });
  }

  /** True if this reel is currently held — host should skip spinning it next round. */
  isHeld(): boolean {
    return this.held;
  }

  /**
   * Hosts call between spins to enable tap-to-hold; pass {@code false} when a
   * spin is in progress to lock the held state. Disabling also clears any
   * current hold so a leftover badge can't survive a spin.
   */
  setHoldInteractive(value: boolean): void {
    this.holdInteractive = value;
    this.eventMode = value ? 'static' : 'auto';
    this.cursor = value ? 'pointer' : 'default';
    // 2026-06-04 — used to clear `this.held` on disable so a stale
    // badge couldn't survive a spin. That clobbered the held state
    // BEFORE the spin runner saw it (handleSpin calls
    // setHoldInteractive(false) at line 866 then awaits the spin),
    // which is why client-side hold never actually held anything.
    // SlotMachine's stopOnOutcomeInternal clears held flags at the
    // END of a hold-respecting spin (line ~291), so the cleanup
    // happens at the right time and a leftover badge can't survive.
  }

  /** Toggle the held flag. Visible feedback (border + HOLD badge) updates immediately. */
  setHeld(value: boolean): void {
    if (this.held === value) return;
    this.held = value;
    this.holdUI.setHeld(value);
  }

  /** Begin a free-spin scroll. {@link stop} ends it and reveals the given final symbols.
   * No-op if the reel is currently held — held reels keep their visible symbols. */
  spin(): void {
    if (this.isSpinning) {
      return;
    }
    if (this.held) {
      return;
    }
    // Reset any leftover win-celebration imagery on this reel BEFORE
    // the strip starts scrolling. Without this, mid-playback videos
    // and held trophy frames from the previous round can linger
    // visibly into the start of the new spin (the user presses SPIN
    // before the celebration finishes; the reel scrolls past cells
    // whose video sprite is still composited on top).
    for (const symbol of this.symbols) {
      symbol.resetCelebration();
    }
    this.isSpinning = true;
    this.isStopping = false;

    this.freeSpinHandler = (tick: Ticker) => {
      const dy = (this.scrollSpeed * tick.deltaMS) / 1000;
      this.advanceStrip(dy);
    };
    this.ticker.add(this.freeSpinHandler);
  }

  /**
   * End the free spin and ease the strip to a position where the visible window
   * shows exactly the given {@code finalSymbols}, top-to-bottom.
   *
   * <p>The strip continues scrolling through the deceleration — there is no
   * snap at the moment of stop. The target symbols are queued and fed through
   * the existing wrap mechanism, so they enter from above the visible window
   * naturally and decelerate to rest with the rest of the strip. Because the
   * symbol changes happen above the mask (where they're invisible), the user
   * never sees a swap — only smooth, decelerating motion that lands on the
   * right outcome.
   *
   * <p>Was: snap-back to {@code y = -symbolSize}, swap every symbol's def at
   * once, animate to rest. The instant of swap was the flicker the user could
   * see at landing — fixed by moving the swap to happen above the visible
   * window during scroll.
   */
  stop(finalSymbols: readonly SlotSymbolDef[], durationMs: number, onComplete?: () => void): void {
    // Leaving the slot mid-spin destroys the reel before the server reply
    // lands and asks it to stop; there is nothing left to stop.
    if (this.destroyed) return;
    if (!this.isSpinning || this.isStopping) {
      return;
    }
    if (finalSymbols.length !== this.visibleRows) {
      throw new Error(
        `stop: expected ${this.visibleRows} final symbols, got ${finalSymbols.length}`,
      );
    }
    this.isStopping = true;

    if (this.freeSpinHandler) {
      this.ticker.remove(this.freeSpinHandler);
      this.freeSpinHandler = null;
    }

    // Build the wrap queue. Each wrap pops the bottom symbol, gives it a fresh
    // def, and unshifts it to the top of the strip. After {@code stripCount}
    // wraps (visibleRows + 2 = 5 by default) every position has been replaced
    // and the array reads, top-to-bottom:
    //   [queue[4], queue[3], queue[2], queue[1], queue[0]]
    // For final state [random-above, finalSymbols[0..N-1], random-below],
    // queue must be (FIFO):
    //   [random-below, finalSymbols[N-1], ..., finalSymbols[0], random-above]
    const stripCount = this.symbols.length;
    const reversedFinal = finalSymbols.slice().reverse();
    this.wrapQueue = [
      this.weightedPick(),
      ...reversedFinal,
      this.weightedPick(),
    ];
    // Sanity: the queue length should equal stripCount so every position is
    // overwritten by the time we land. If config changes (visibleRows etc.),
    // the assertion catches the bug early.
    if (this.wrapQueue.length !== stripCount) {
      throw new Error(
        `stop: wrap queue (${this.wrapQueue.length}) must equal strip count (${stripCount})`,
      );
    }

    // Travel: enough distance for {@code stripCount} wraps so the queue is fully
    // consumed. Strip currently sits at {@code currentY} in [0, symbolSize).
    // First wrap takes (symbolSize - currentY); subsequent wraps each take
    // symbolSize. Total = stripCount * symbolSize - currentY.
    const startY = this.strip.y;
    const totalDistance = stripCount * this.symbolSize - startY;
    let appliedDistance = 0;

    this.stopHandle = tween({
      durationMs,
      // easeOutQuart is monotonic — needed because advanceStrip mutates state
      // forward and can't be replayed. easeOutBack's mid-curve overshoot would
      // consume queue entries too early. The "thunk" feel comes from a small
      // separate y bounce after the main deceleration completes.
      easing: Easings.easeOutQuart,
      onUpdate: (progress) => {
        const targetDistance = totalDistance * progress;
        const dy = targetDistance - appliedDistance;
        appliedDistance = targetDistance;
        if (dy > 0) {
          this.advanceStrip(dy);
        }
      },
      onComplete: () => {
        // Drain any wrapQueue entries the tween's onUpdate path left
        // behind. easeOutQuart's tail can land strip.y just shy of
        // symbolSize on the final frame, missing the last wrap and
        // leaving the visible window off-by-one — bonus cells then
        // render whichever symbol the strip happens to hold at that
        // row. Each leftover entry is one missed wrap, so consume it
        // here using the same pop-bottom/unshift-top mechanic.
        while (this.wrapQueue.length > 0) {
          const lifted = this.symbols.pop();
          if (!lifted) break;
          const nextDef = this.wrapQueue.shift()!;
          lifted.setDef(nextDef);
          this.symbols.unshift(lifted);
        }
        // Snap exact rest to absorb floating-point drift.
        this.strip.y = 0;
        for (let i = 0; i < this.symbols.length; i++) {
          this.symbols[i]!.y = (i - 1) * this.symbolSize + this.symbolSize / 2;
        }
        this.isSpinning = false;
        this.isStopping = false;
        this.stopHandle = null;

        // "Thunk" effect — short y bobble so the stop still feels physical.
        this.applyThunk();

        onComplete?.();
      },
    });
  }

  /** Damped two-phase landing — gives the reel stop a physical
   *  "weight" feel. Producer brief 2026-06-04: "I don't feel the
   *  physics in the reels landing like the old slots."
   *
   *  <p>The previous single 6px arc read as a soft wobble — barely
   *  perceptible at the 180ms duration. Real mechanical slots have a
   *  brake-engage moment where the reel overshoots the stop position
   *  from its own inertia, then springs back via the stop pin's
   *  reaction force. We mimic that with two arcs of decreasing
   *  amplitude:
   *
   *  <ul>
   *    <li>Phase A (0 → 45%): 0 → +DOWN_AMP → 0
   *        — compression downward (the strip carries momentum
   *        past zero before the stop pin catches it).
   *    <li>Phase B (45 → 100%): 0 → -UP_AMP → 0
   *        — rebound upward, smaller amplitude (energy is lost
   *        to the impact, so the recoil is shallower).
   *  </ul>
   *
   *  <p>Amplitudes bumped from 6px to 12px down + 5px up; duration
   *  bumped from 180ms to 300ms so the damped wobble has time to
   *  read at a comfortable cadence rather than zipping past in a
   *  blur. The landing thunk still fires at the moment of land
   *  (deceleration tween's onComplete in SlotMachine), which now
   *  lands exactly at the start of Phase A — so the audio "thump"
   *  and the visible compression hit at the same beat. */
  private applyThunk(): void {
    this.thunkHandle?.cancel();
    const DOWN_AMP = 12;
    const UP_AMP = 5;
    const PHASE_A_END = 0.45;
    this.thunkHandle = tween({
      durationMs: 300,
      easing: Easings.linear, // shape comes from the per-phase sin curves
      onUpdate: (p) => {
        // Guard against teardown mid-tween: when the reel is destroyed,
        // Pixi nullifies the strip's internal transform trackers and
        // {@code strip.y = ...} throws. The destroy path also cancels
        // this handle, so in practice the guard is belt-and-braces.
        if (this.strip.destroyed) return;
        if (p < PHASE_A_END) {
          // Phase A — downward overshoot. sin(0..π) → 0 → 1 → 0.
          const sub = p / PHASE_A_END;
          this.strip.y = Math.sin(sub * Math.PI) * DOWN_AMP;
        } else {
          // Phase B — upward rebound, smaller amplitude. sin(0..π)
          // again but negated so the strip moves UP past zero before
          // returning to rest.
          const sub = (p - PHASE_A_END) / (1 - PHASE_A_END);
          this.strip.y = -Math.sin(sub * Math.PI) * UP_AMP;
        }
      },
      onComplete: () => {
        this.thunkHandle = null;
        if (this.strip.destroyed) return;
        this.strip.y = 0;
      },
    });
  }

  /** Returns the currently visible symbol defs (top-to-bottom), only meaningful at rest. */
  visibleSymbolDefs(): SlotSymbolDef[] {
    const result: SlotSymbolDef[] = [];
    for (let i = 1; i <= this.visibleRows; i++) {
      result.push(this.symbols[i]!.def);
    }
    return result;
  }

  /**
   * Celebrate the visible symbol at the given row — preferred path
   * called from {@link SlotMachine#highlightWins}. When the symbol's
   * theme ships a win-frame spritesheet (asset brief pack 2 §22), play
   * the per-symbol AnimatedSprite once and resolve when its final
   * frame fires. Otherwise fall back to {@link flashCellAtRow}'s
   * scale pulse so emoji-only / partial-art themes still draw the eye
   * to the winning cell.
   *
   * <p>No-op (resolves immediately) if {@code row} is out of range.
   */
  async celebrateCellAtRow(row: number): Promise<void> {
    if (row < 0 || row >= this.visibleRows) return;
    const symbol = this.symbols[row + 1];
    if (!symbol) return;
    // Prefer the per-symbol video celebration when one is wired and
    // loaded; otherwise fall back to the scale-pulse so non-money
    // cells (cherry / seven / gem etc.) still get a visible win cue.
    const videoAlias = symbol.def.winVideoAlias;
    const video = videoAlias ? AssetLoader.shared.getVideo(videoAlias) : null;
    if (video) {
      await symbol.playWinAnimation();
    } else {
      this.flashCellAtRow(row);
    }
  }

  /**
   * Item-drop reveal: swap the visible symbol at {@code row} through
   * a sequence of {@code cycleDefs} (rapid texture swap, easing out),
   * then settle on {@code finalDef} with a scale-punch. Returns a
   * promise that resolves when the settle finishes.
   *
   * <p>Driven by {@link SlotMachine#playItemDropReveal} after the reel
   * lands on an {@code item_drop} cell. The reel's own scroll logic
   * is paused while this animation runs (the spin is already
   * complete; the cell is settled), so the symbol stays put while
   * its {@code def} field rotates underneath it.
   *
   * <p>Each cycle frame constructs a unique {@code def.id} to bypass
   * {@link SlotSymbol#setDef}'s same-id early-exit; otherwise the
   * texture wouldn't refresh between identical-id swaps.
   */
  async cycleAndSettleAtRow(
    row: number,
    cycleDefs: readonly SlotSymbolDef[],
    finalDef: SlotSymbolDef,
    options: { cycleDurationMs?: number; settleDurationMs?: number } = {},
  ): Promise<void> {
    if (row < 0 || row >= this.visibleRows) return;
    const symbol = this.symbols[row + 1];
    if (!symbol || cycleDefs.length === 0) return;

    // Producer brief 2026-05-13: reveal total tightened to 1000ms
    // (cycle 750 + settle 250). Pulse overlaps with cycle (fire-and-
    // forget in {@link SlotMachine#playItemDropReveal}) so doesn't
    // add to perceived duration. The sqrt(p) index curve still gives
    // decelerating swaps, just compressed.
    const cycleMs = options.cycleDurationMs ?? 750;
    const settleMs = options.settleDurationMs ?? 250;

    // Phase B — cycle. Tween from 0..1 over cycleMs, decelerating
    // the texture-swap cadence so the cell visibly slows into the
    // settle (roulette-style "click … click … click"). Producer
    // brief 2026-05-13: easeOutQuart raced through the full list in
    // the first ~50% of the cycle then held on the last frame —
    // reads as "blur, then stop" rather than "gradually slowing."
    //
    // Using √p as the index curve gives a much gentler deceleration:
    // for N=10 items, swap 1 fires at ~1% of duration, swap 5 at
    // ~25%, swap 8 at ~64%, swap 9 at ~81%, swap 10 at 100%. The
    // intervals between swaps grow visibly larger as the cycle ends,
    // selling the "wheel slowing into place" feel.
    let lastIndex = -1;
    await new Promise<void>((resolve) => {
      tween({
        durationMs: cycleMs,
        easing: Easings.linear,
        onUpdate: (p) => {
          // The slot was taken down mid-reveal (see flashCellAtRow).
          if (symbol.destroyed) return;
          const eased = Math.sqrt(p);
          const index = Math.min(
            cycleDefs.length - 1,
            Math.floor(eased * cycleDefs.length),
          );
          if (index !== lastIndex) {
            lastIndex = index;
            const def = cycleDefs[index]!;
            // Force a refresh per cycle frame even when the same def
            // would otherwise be deduped by setDef's id-equality guard.
            symbol.def = { ...def, id: '_cycle:' + p.toFixed(4) };
            symbol.setDef(def);
          }
        },
        onComplete: () => resolve(),
      });
    });

    // Phase C — settle. Swap to the final def and scale-punch.
    symbol.def = { ...finalDef, id: '_cycle:settle' };
    symbol.setDef(finalDef);
    const baseScale = 1;
    const peakScale = 1.18;
    await new Promise<void>((resolve) => {
      tween({
        durationMs: settleMs,
        easing: Easings.linear,
        onUpdate: (p) => {
          if (symbol.destroyed) return;
          // Single peak — scale up then back to base over the duration.
          const t = p < 0.5 ? p * 2 : 2 - p * 2;
          symbol.scale.set(baseScale + (peakScale - baseScale) * Easings.easeOutCubic(t));
        },
        onComplete: () => {
          if (!symbol.destroyed) symbol.scale.set(baseScale);
          resolve();
        },
      });
    });
  }

  /**
   * Pulse the visible symbol at the given row (0-indexed top) — a quick
   * scale-up + scale-back, repeated twice. Used by SlotMachine.highlightWins
   * to draw the eye to winning cells. No-op if {@code row} is out of range.
   */
  flashCellAtRow(row: number, totalDurationMs: number = 1100): TweenHandle | null {
    if (row < 0 || row >= this.visibleRows) {
      return null;
    }
    const symbol = this.symbols[row + 1];
    if (!symbol) return null;

    const baseScale = 1;
    const peakScale = 1.18;

    // Two pulses inside totalDurationMs.
    const handle = tween({
      durationMs: totalDurationMs,
      easing: Easings.linear,
      onUpdate: (p) => {
        // Guard against teardown mid-pulse: a slot can be taken down while
        // its wins still flash (it remounts when the server switches its
        // road on or off), and a destroyed symbol's scale is gone.
        if (symbol.destroyed) return;
        // Two cycles: scale up + back, twice.
        const phase = (p * 2) % 1;
        const t = phase < 0.5 ? phase * 2 : 2 - phase * 2;
        symbol.scale.set(baseScale + (peakScale - baseScale) * Easings.easeOutCubic(t));
      },
      onComplete: () => {
        if (!symbol.destroyed) symbol.scale.set(baseScale);
      },
    });
    return handle;
  }

  /**
   * Strip advancement: shift the strip down by {@code dy} pixels, wrapping any
   * symbol that has fallen below the bottom row back to the top.
   *
   * <p>Used by both free-spin (constant-velocity scroll) and stop (decelerating
   * scroll). When the {@link wrapQueue} is non-empty, the next def for a
   * wrapped symbol comes from the queue — this is how stop() makes the target
   * symbols enter the visible window naturally rather than snap-swapping at
   * the moment of land.
   */
  private advanceStrip(dy: number): void {
    this.strip.y += dy;
    while (this.strip.y >= this.symbolSize) {
      this.strip.y -= this.symbolSize;
      const lifted = this.symbols.pop();
      if (lifted) {
        const nextDef = this.wrapQueue.length > 0
          ? this.wrapQueue.shift()!
          : this.weightedPick();
        lifted.setDef(nextDef);
        this.symbols.unshift(lifted);
        for (let i = 0; i < this.symbols.length; i++) {
          this.symbols[i]!.y = (i - 1) * this.symbolSize + this.symbolSize / 2;
        }
      }
    }
  }

  private weightedPick(): SlotSymbolDef {
    const totalWeight = this.availableSymbols.reduce((sum, s) => sum + s.weight, 0);
    let pick = this.random.nextFloat() * totalWeight;
    for (const symbol of this.availableSymbols) {
      pick -= symbol.weight;
      if (pick <= 0) {
        return symbol;
      }
    }
    return this.availableSymbols[this.availableSymbols.length - 1]!;
  }

  /**
   * Recovery path for the host: abandon any in-flight spin/stop and
   * snap the reel to a clean idle state. Visible symbols are whatever
   * happens to be in the strip at cancel time — callers reach this
   * path because the real outcome never arrived (server failure /
   * timeout), so re-rolling would be misleading. After this returns,
   * a fresh {@link spin} is safe.
   */
  forceLand(): void {
    if (this.freeSpinHandler) {
      this.ticker.remove(this.freeSpinHandler);
      this.freeSpinHandler = null;
    }
    this.stopHandle?.cancel();
    this.stopHandle = null;
    this.strip.y = 0;
    for (let i = 0; i < this.symbols.length; i++) {
      this.symbols[i]!.y = (i - 1) * this.symbolSize + this.symbolSize / 2;
    }
    this.wrapQueue = [];
    this.isSpinning = false;
    this.isStopping = false;
  }

  destroyReel(): void {
    if (this.freeSpinHandler) {
      this.ticker.remove(this.freeSpinHandler);
      this.freeSpinHandler = null;
    }
    this.stopHandle?.cancel();
    this.stopHandle = null;
    this.thunkHandle?.cancel();
    this.thunkHandle = null;
  }
}
